import { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
} from '@mui/icons-material';
import type { LocationOption } from './IssuePlannerTable';

export type IssueAdditionalItemRow = {
  groupId: string;
  lin: string | null;
  nsn: string | null;
  name: string;
  sizeLabel?: string | null;
  availableQty: number;
  availableAssetIds: string[];
};

export type IssueAdditionalItemDisplayRow = IssueAdditionalItemRow & {
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
};

export interface IssueAdditionalItemsTableProps {
  isLoading: boolean;
  isLoadingMore?: boolean;
  rows: IssueAdditionalItemDisplayRow[];
  onAddItem: (row: IssueAdditionalItemRow) => void;
  onToggleExpand: (groupId: string) => void;
  readOnly?: boolean;
  /** Per-item location options (grouped: Primary, Secondary, Related). */
  locationOptionsByGroupId: Map<string, LocationOption[]>;
  /** Currently selected location ID per group (defaults to primary). */
  selectedLocationByGroupId: Record<string, string | undefined>;
  /** Called when user picks a location for an item. */
  onLocationChange: (groupId: string, locationId: string) => void;
  /** Whether location data is still loading. */
  locationsLoading?: boolean;
}

export const IssueAdditionalItemsTable = ({
  isLoading,
  isLoadingMore = false,
  rows,
  onAddItem,
  onToggleExpand,
  readOnly = false,
  locationOptionsByGroupId,
  selectedLocationByGroupId,
  locationsLoading = false,
  onLocationChange,
}: IssueAdditionalItemsTableProps) => {
  const rootCount = useMemo(
    () => rows.filter((row) => row.depth === 0).length,
    [rows],
  );

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', maxHeight: '70vh', position: 'relative' }}>
      {isLoadingMore && (
        <LinearProgress
          variant="indeterminate"
          sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 3 }}
        />
      )}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {rows.length} items ({rootCount} top-level)
            </Typography>
          </Box>
          <Box sx={{ overflow: 'auto', maxHeight: 'calc(70vh - 52px)' }}>
            <Table stickyHeader size="small" aria-label="Additional inventory items">
              <TableHead>
                <TableRow>
                  <TableCell>LIN</TableCell>
                  <TableCell>NSN</TableCell>
                  <TableCell>Nomenclature</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Availability</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No additional inventory available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const availableCount = row.availableQty;
                    const hasAvailability = availableCount > 0;
                    const locationOptions = locationOptionsByGroupId.get(row.groupId) ?? [];
                    const selectableOptions = locationOptions.filter((option) => !option.disabled);
                    const hasLocation = selectableOptions.length > 0;
                    // Default to the first option with stock; fall back to the first option overall
                    const defaultLocationId = selectableOptions.find((o) => (o.qty ?? 0) > 0)?.id ?? selectableOptions[0]?.id ?? '';
                    const selectedLocationOutOfStock = (() => {
                      if (selectableOptions.length === 0) return false;
                      const selectedLocId = selectedLocationByGroupId[row.groupId] ?? defaultLocationId;
                      const selectedOpt = selectableOptions.find((o) => o.id === selectedLocId);
                      return selectedOpt != null && selectedOpt.qty === 0;
                    })();
                    const canAdd = hasAvailability && hasLocation && !selectedLocationOutOfStock && !readOnly && !row.hasChildren;
                    const isParent = row.hasChildren;
                    const availabilityChip = hasAvailability
                      ? { label: `${availableCount} Available`, color: isParent ? 'default' as const : 'success' as const }
                      : { label: 'Unavailable', color: 'default' as const };
                    const actionCursor = canAdd ? 'pointer' : 'default';
                    const isChild = row.depth > 0;
                    const isOutOfStock =
                      !isParent && (!hasAvailability || !hasLocation || selectedLocationOutOfStock);

                    return (
                      <TableRow
                        key={row.groupId}
                        hover={!isParent && !isOutOfStock}
                        sx={{
                          ...(isOutOfStock ? { opacity: 0.5 } : undefined),
                          ...(isChild ? { backgroundColor: 'action.hover' } : undefined),
                          ...(isParent ? { cursor: 'default' } : undefined),
                        }}
                      >
                        <TableCell>{row.lin ?? '—'}</TableCell>
                        <TableCell>{row.nsn ?? '—'}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              pl: row.depth * 2,
                              gap: 0.5,
                              height: '100%',
                            }}
                          >
                            {row.hasChildren ? (
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onToggleExpand(row.groupId);
                                }}
                                aria-label={row.isExpanded ? 'Collapse item' : 'Expand item'}
                                sx={{ p: 0.5 }}
                              >
                                {row.isExpanded ? (
                                  <ArrowDownIcon fontSize="small" />
                                ) : (
                                  <ArrowRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            ) : (
                              <Box component="span" sx={{ width: 32, display: 'inline-flex' }} />
                            )}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2">
                                {row.sizeLabel && !row.name.includes(`(${row.sizeLabel})`) ? `${row.name} (${row.sizeLabel})` : row.name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 140 }}>
                          {!row.hasChildren && (() => {
                            const options = locationOptionsByGroupId.get(row.groupId) ?? [];
                            const selectableOptions = options.filter((o) => !o.disabled);
                            const effectiveDefault = selectableOptions.find((o) => (o.qty ?? 0) > 0)?.id ?? selectableOptions[0]?.id ?? '';
                            const selectedLocId = selectedLocationByGroupId[row.groupId] ?? effectiveDefault;

                            if (selectableOptions.length === 0) {
                              return locationsLoading ? (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  Loading…
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="error" noWrap>
                                  No location
                                </Typography>
                              );
                            }

                            if (selectableOptions.length === 1) {
                              const opt = selectableOptions[0]!;
                              const displayLabel = opt.qty != null ? `${opt.label} (${opt.qty})` : opt.label;
                              return (
                                <Typography variant="caption" noWrap title={displayLabel}>
                                  {displayLabel}
                                </Typography>
                              );
                            }

                            return (
                              <Select
                                size="small"
                                value={selectedLocId}
                                onChange={(event) => {
                                  onLocationChange(row.groupId, event.target.value as string);
                                }}
                                disabled={readOnly}
                                variant="standard"
                                disableUnderline
                                sx={{ fontSize: '0.75rem', maxWidth: 130 }}
                              >
                                {selectableOptions.map((opt, idx) => {
                                  const prevGroup = idx > 0 ? selectableOptions[idx - 1]?.group : undefined;
                                  const showHeader = opt.group && opt.group !== prevGroup;
                                  const optLabel = opt.qty != null ? `${opt.label} (${opt.qty})` : opt.label;
                                  return [
                                    showHeader && (
                                      <ListSubheader key={`header-${opt.group}`} sx={{ lineHeight: '24px', fontSize: '0.7rem' }}>
                                        {opt.group}
                                      </ListSubheader>
                                    ),
                                    <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: '0.75rem', minHeight: 28 }}>
                                      {optLabel}
                                    </MenuItem>,
                                  ];
                                })}
                              </Select>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            color={availabilityChip.color}
                            variant={isParent ? 'outlined' : 'filled'}
                            label={isParent && hasAvailability ? `${availableCount} Total` : availabilityChip.label}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {!row.hasChildren && (
                            <Box component="span" sx={{ display: 'inline-flex', cursor: actionCursor }}>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="Add to cart"
                                disabled={!canAdd}
                                sx={{ cursor: actionCursor }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onAddItem(row);
                                }}
                              >
                                <AddIcon fontSize="inherit" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Paper>
  );
};
